import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { InferType } from 'yup';

import { Route } from 'RestAPIs/travel-distances/types';

import { Box, Button, DialogActions, Grid, TextField } from '@mui/material';

import { Dialog, Select } from 'components/shared';

import { useSaveTravelDistanceMutation } from '../queries';
import getSchema from '../schema';
import AddressForm from './AddressForm';

const schema = getSchema();
type TravelDistanceForm = InferType<typeof schema>;

type Props = {
  route?: Route | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
};

const AddEditTravelDistanceDialog = ({ route, isOpen, onClose }: Props) => {
  const { t } = useTranslation();

  const { control, handleSubmit, reset, ...rest } = useForm<TravelDistanceForm>(
    {
      resolver: yupResolver(getSchema())
    }
  );

  const { mutate: saveTravelDistance } = useSaveTravelDistanceMutation();

  useEffect(() => {
    if (route) {
      reset({
        ...route,
        distanceInMeters: route.distanceInMeters / 1000,
        travelTimeInSeconds: route.travelTimeInSeconds / 60
      });
    }
  }, [route]);

  const onClickCancel = () => {
    onClose();
  };

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      distanceInMeters: data.distanceInMeters * 1000,
      travelTimeInSeconds: data.travelTimeInSeconds * 60
    };

    await saveTravelDistance(payload);
  };

  return (
    <Dialog
      open={isOpen}
      title={
        route && route.id
          ? t('travelDistances.editTravelDistance')
          : t('travelDistances.addTravelDistance')
      }
      handleClose={onClickCancel}
      PaperProps={{
        sx: { minWidth: 600 }
      }}
    >
      <Box>
        <FormProvider
          control={control}
          reset={reset}
          handleSubmit={handleSubmit}
          {...rest}
        >
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <AddressForm formKey="departureAddress" />
            <AddressForm formKey="destinationAddress" />
            <Grid container spacing={2} mt={3}>
              <Grid item xs={6}>
                <Controller
                  name="distanceInMeters"
                  control={control}
                  render={({ field, fieldState }) => (
                    <NumericFormat
                      {...field}
                      allowNegative={false}
                      decimalScale={3}
                      customInput={TextField}
                      type="text"
                      onValueChange={({ value: v }) =>
                        field.onChange({ target: { value: v } })
                      }
                      label={t('travelDistances.distance')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message ?? null}
                      decimalSeparator=","
                      valueIsNumericString
                      fullWidth
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="travelTimeInSeconds"
                  control={control}
                  render={({ field, fieldState }) => (
                    <NumericFormat
                      {...field}
                      allowNegative={false}
                      customInput={TextField}
                      type="text"
                      onValueChange={({ value: v }) =>
                        field.onChange({ target: { value: v } })
                      }
                      label={t('travelDistances.travelTimeInMinutes')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message ?? null}
                      decimalSeparator=","
                      valueIsNumericString
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={6}>
                <Controller
                  name="transportationMode"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Select
                      fullWidth
                      {...field}
                      onSelected={field.onChange}
                      label={t('travelDistances.transportationMode')}
                      options={Object.keys(
                        t('travelDistances.transportationModes')
                      ).map((key) => ({
                        label: t(`travelDistances.transportationModes.${key}`),
                        value: key
                      }))}
                      error={fieldState.error}
                      valueProp={field.value}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="routeCalculationType"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Select
                      fullWidth
                      {...field}
                      onSelected={field.onChange}
                      label={t('travelDistances.routeCalculationType')}
                      options={Object.keys(
                        t('travelDistances.calculationTypes')
                      ).map((key) => ({
                        label: t(`travelDistances.calculationTypes.${key}`),
                        value: key
                      }))}
                      error={fieldState.error}
                      valueProp={field.value}
                      required
                    />
                  )}
                />
              </Grid>
            </Grid>
            <DialogActions sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={onClickCancel}
              >
                {t('cancel')}
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {t('save')}
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Box>
    </Dialog>
  );
};

export default AddEditTravelDistanceDialog;
